import { useEffect, useRef, useState } from 'react';
import { audioContext } from '@/audio/context';
import type { PoseId } from '@/content/articulation';
import { cx } from '@/lib/cx';
import { blendShapes, shapeOf, TongueScene } from '@/ui/tongue3d/TongueScene';
import s from './debug.module.css';
import {
  frameAt,
  place,
  TIMELINE_DEFAULTS,
  type Segment,
  type TimelineOptions,
} from './renderTimeline';
import { Card } from './ui';

const POSES: readonly PoseId[] = ['rest', 'rBunched', 'rRetroflex', 'w', 'l'];

interface Row extends Segment {
  id: number;
  /** The decoded recording, if one was chosen (its length sets `seconds`). */
  audio: AudioBuffer | null;
  file: string | null;
}

let nextId = 1;
const row = (pose: PoseId, caption: string): Row => ({
  id: nextId++,
  pose,
  caption,
  seconds: 3,
  audio: null,
  file: null,
});

/** A recorder format this browser can write: WebM (Chrome, Firefox) or MP4 (Safari). */
function recorderType(): string {
  const types = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  return types.find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
}

/**
 * Render: the 3D mouth saying a sequence of held sounds, synced to recordings, previewed here
 * and recorded as a video with its sound and captions. Each row is a pose, its recording (or a
 * length) and a caption; the mouth moves into each pose just before its sound, holds while the
 * camera turns, and relaxes after the last (renderTimeline.ts). The animation follows the audio
 * clock, so picture and sound stay in step. The same timeline renders frame by frame offline:
 * scripts/render-rw-video.mjs.
 */
export function RenderPanel() {
  const host = useRef<HTMLDivElement>(null);
  const scene = useRef<TongueScene | null>(null);
  const [rows, setRows] = useState<Row[]>(() => [row('rBunched', 'rrr'), row('w', 'www')]);
  const [options, setOptions] = useState<TimelineOptions>(TIMELINE_DEFAULTS);
  const [captions, setCaptions] = useState(true);
  const [busy, setBusy] = useState<'preview' | 'record' | null>(null);
  const [caption, setCaption] = useState<string | null>(null);
  const [video, setVideo] = useState<{ url: string; name: string; bytes: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const stop = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!host.current) return;
    const sc = new TongueScene(host.current, { preserveDrawingBuffer: true, orbitControls: false });
    scene.current = sc;
    sc.setBlend('rest', 'rest', 1);
    return () => {
      stop.current?.();
      sc.dispose();
    };
  }, []);

  useEffect(
    () => () => {
      if (video) URL.revokeObjectURL(video.url);
    },
    [video],
  );

  const update = (id: number, patch: Partial<Row>) => {
    setRows((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const loadAudio = async (id: number, file: File | undefined) => {
    if (!file) return;
    try {
      const buffer = await audioContext().decodeAudioData(await file.arrayBuffer());
      update(id, { audio: buffer, file: file.name, seconds: buffer.duration });
    } catch (e) {
      setError(`Could not read ${file.name}: ${String(e)}`);
    }
  };

  /** Plays the sequence; with `record`, also records the picture (with captions) and sound. */
  const run = async (record: boolean) => {
    const sc = scene.current;
    if (!sc || busy) return;
    setError(null);
    await sc.ready;
    const ctx = audioContext();
    if (ctx.state === 'suspended') await ctx.resume();
    const { placed, total } = place(rows, options);
    const dest = record ? ctx.createMediaStreamDestination() : null;
    // Silence, always flowing into the recording: an audio track with no samples (no clip
    // playing yet, or none at all) made the recorder write nothing (0 bytes).
    const keepAlive = dest ? ctx.createConstantSource() : null;
    if (keepAlive && dest) {
      keepAlive.offset.value = 0;
      keepAlive.connect(dest);
      keepAlive.start();
    }
    const t0 = ctx.currentTime + 0.3;
    const sources = rows.flatMap((r, i) => {
      if (!r.audio) return [];
      const src = ctx.createBufferSource();
      src.buffer = r.audio;
      src.connect(ctx.destination);
      if (dest) src.connect(dest);
      src.start(t0 + (placed[i]?.start ?? 0));
      return [src];
    });

    // The recorded picture: the 3D frame with the caption drawn over it.
    let recorder: MediaRecorder | null = null;
    const chunks: Blob[] = [];
    const frame = document.createElement('canvas');
    // In the page (off screen): a detached canvas gave the recorder no frames.
    Object.assign(frame.style, { position: 'fixed', left: '-10000px', top: '0' });
    let track: CanvasCaptureMediaStreamTrack | undefined;
    if (record && dest) {
      frame.width = sc.canvas.width;
      frame.height = sc.canvas.height;
      document.body.append(frame);
      // Frames are pushed as they are drawn (requestFrame), not sampled on a timer.
      const video = frame.captureStream(0).getVideoTracks();
      track = video[0] as CanvasCaptureMediaStreamTrack | undefined;
      const type = recorderType();
      recorder = new MediaRecorder(new MediaStream([...video, ...dest.stream.getAudioTracks()]), {
        ...(type ? { mimeType: type } : {}),
        videoBitsPerSecond: 8_000_000,
      });
      recorder.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      recorder.start(1000);
    }
    setBusy(record ? 'record' : 'preview');
    let raf = 0;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      cancelAnimationFrame(raf);
      keepAlive?.stop();
      for (const src of sources) {
        try {
          src.stop();
        } catch {
          // already ended
        }
      }
      setCaption(null);
      setBusy(null);
      stop.current = null;
      if (recorder) {
        const type = recorder.mimeType || 'video/webm';
        recorder.onstop = () => {
          frame.remove();
          const blob = new Blob(chunks, { type });
          const name = `mouth-${rows.map((r) => r.caption || r.pose).join('-')}.${type.includes('mp4') ? 'mp4' : 'webm'}`;
          setVideo({ url: URL.createObjectURL(blob), name, bytes: blob.size });
        };
        recorder.stop();
      }
    };
    stop.current = finish;
    const draw = () => {
      const t = ctx.currentTime - t0;
      const f = frameAt(Math.max(0, t), placed, options);
      sc.setShape(blendShapes(shapeOf(f.from), shapeOf(f.to), f.amount));
      sc.setTurn(f.turn);
      const text = captions ? f.caption : null;
      setCaption(text);
      if (recorder) {
        const g = frame.getContext('2d');
        if (g) {
          g.fillStyle = '#e8f0fb';
          g.fillRect(0, 0, frame.width, frame.height);
          g.drawImage(sc.canvas, 0, 0);
          if (text) {
            g.font = `bold ${String(Math.round(frame.height / 9))}px system-ui, sans-serif`;
            g.textAlign = 'center';
            g.fillStyle = '#8a1f2d';
            g.fillText(text, frame.width / 2, frame.height * 0.93);
          }
          track?.requestFrame();
        }
      }
      if (t >= total) finish();
      else raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
  };

  const { total } = place(rows, options);
  const num = (key: keyof TimelineOptions, label: string, step = 0.1, max = 3) => (
    <label className={s.label}>
      {label}
      <input
        className={s.input}
        type="number"
        min={0}
        max={max}
        step={step}
        value={options[key]}
        onChange={(e) => {
          setOptions({ ...options, [key]: Number(e.currentTarget.value) });
        }}
      />
    </label>
  );

  return (
    <>
      <Card
        title="Render a demonstration"
        actions={<span className={cx(s.badge, s.mono)}>{total.toFixed(1)} s</span>}
      >
        <div
          ref={host}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: 720,
            aspectRatio: '4 / 3',
            background: '#e8f0fb',
            borderRadius: 8,
          }}
        >
          {caption && (
            <span
              style={{
                position: 'absolute',
                bottom: '4%',
                left: 0,
                right: 0,
                textAlign: 'center',
                font: '700 2.4rem system-ui',
                color: '#8a1f2d',
              }}
            >
              {caption}
            </span>
          )}
        </div>
        <div className={s.row} style={{ marginTop: 12 }}>
          <button
            type="button"
            className={s.btn}
            disabled={busy !== null}
            onClick={() => void run(false)}
          >
            ▶ Preview
          </button>
          <button
            type="button"
            className={cx(s.btn, s.primary)}
            disabled={busy !== null}
            onClick={() => void run(true)}
          >
            ● Record video
          </button>
          <button
            type="button"
            className={s.btn}
            disabled={busy === null}
            onClick={() => stop.current?.()}
          >
            ■ Stop
          </button>
          {video && (
            <a className={s.btn} href={video.url} download={video.name}>
              ⤓ {video.name} ({(video.bytes / 1e6).toFixed(1)} MB)
            </a>
          )}
        </div>
        {error && <p role="alert">{error}</p>}
      </Card>

      <Card
        title="Sounds"
        actions={
          <button
            type="button"
            className={cx(s.btn, s.small)}
            onClick={() => {
              setRows((rs) => [...rs, row('rBunched', 'rrr')]);
            }}
          >
            + Add
          </button>
        }
      >
        {rows.map((r, i) => (
          <div key={r.id} className={s.row} style={{ flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <span className={s.mono}>{i + 1}.</span>
            <select
              className={s.select}
              aria-label="Pose"
              value={r.pose}
              onChange={(e) => {
                update(r.id, { pose: e.currentTarget.value as PoseId });
              }}
            >
              {POSES.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <input
              className={s.input}
              aria-label="Caption"
              value={r.caption}
              style={{ width: '8em' }}
              onChange={(e) => {
                update(r.id, { caption: e.currentTarget.value });
              }}
            />
            <label className={s.label}>
              {r.file ?? 'Recording'}
              <input
                type="file"
                accept="audio/*"
                onChange={(e) => void loadAudio(r.id, e.currentTarget.files?.[0])}
              />
            </label>
            <label className={s.label}>
              seconds
              <input
                className={s.input}
                type="number"
                min={0.5}
                max={20}
                step={0.1}
                value={Number(r.seconds.toFixed(2))}
                disabled={r.audio !== null}
                style={{ width: '5em' }}
                onChange={(e) => {
                  update(r.id, { seconds: Number(e.currentTarget.value) });
                }}
              />
            </label>
            <button
              type="button"
              className={cx(s.btn, s.small)}
              disabled={rows.length <= 1}
              onClick={() => {
                setRows((rs) => rs.filter((x) => x.id !== r.id));
              }}
            >
              Remove
            </button>
          </div>
        ))}
      </Card>

      <Card title="Timing and camera">
        <div className={s.row} style={{ flexWrap: 'wrap', gap: 12 }}>
          {num('lead', 'Lead (s)')}
          {num('gap', 'Gap (s)')}
          {num('tail', 'Tail (s)')}
          {num('turn', 'Camera turn (0-1)', 0.05, 1)}
          <label className={s.label}>
            <input
              type="checkbox"
              checked={captions}
              onChange={(e) => {
                setCaptions(e.currentTarget.checked);
              }}
            />{' '}
            Captions
          </label>
        </div>
      </Card>
    </>
  );
}
